import { ButtonGroup, Datepicker, Loading, Modal, RadioButton, Popover } from '@brighterion/br-ui';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_RANGE_END, DATE_RANGE_START, getRangeOfDatesRelativeToToday } from 'Utils';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useDeploymentHistory from 'Hooks/query-hooks/revisions/useDeploymentHistory';
import { shouldShowCurrentDate } from 'Selectors';

import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import styles from './NewSimulationModal.css';

type Props = {
    isOpen: boolean;
    closeModal: any;
    handleSubmit?: any;
    isLoading?: boolean;
    hideCompare?: boolean;
};

const NewSimulationModal = ({ isOpen, isLoading, closeModal, handleSubmit, hideCompare }: Props) => {
    const { t } = useTranslation();
    const { users } = useGetReduxUsers();
    const { data: deploymentsData = [] } = useDeploymentHistory(users);

    const endDate = shouldShowCurrentDate() ? DATE_RANGE_END : 0;
    const renderRadio = (value: boolean) => (
        <RadioButton
            name='yes'
            value='yes'
            checked={value}
            text={t('Yes')}
            id='compare-yes'
            disabled={!deploymentsData.length}
        />
    );

    return (
        <Modal isOpen={isOpen} closeModal={closeModal} containerClass={styles.modal} closeOnClickOutside={false}>
            <Modal.Title id='new-simulation-modal-title' title={t('newSimulationTitle')} onClose={closeModal} />
            <Formik
                enableReinitialize
                initialValues={{
                    start: null,
                    simulationType: 'RULE_PERFORMANCE', // TODO hardcoded until field is added to designs
                    sandbox: 'ALL', // TODO hardcoded until field is added to designs
                    duration: 86400000, // TODO hardcoded until field is added to designs
                    compare: !deploymentsData.length ? false : undefined
                }}
                onSubmit={(values) => {
                    handleSubmit(values);
                    trackEvent({
                        eventType: EventTypes.track,
                        eventName: EventNames.click,
                        payload: {
                            clickIdentifier: 'New Simulation - Run Simulation',
                            text: 'Run Simulation'
                        }
                    });
                }}
            >
                {({ values, setFieldValue, dirty, isValid, submitForm }: FormikProps<NewSimulationFormValues>) => {
                    const disable = !dirty || !isValid || !values.start;
                    const disableRunSimulation = !hideCompare
                        ? disable || typeof values.compare !== 'boolean'
                        : disable;
                    const allowedDateRange = getRangeOfDatesRelativeToToday(DATE_RANGE_START, endDate);
                    // TODO: add minDate prop to the date picker in br-ui
                    const latestAllowedDate = allowedDateRange[allowedDateRange.length - 1];

                    return (
                        <>
                            <div className={styles.modalContent}>
                                {`${t('newSimulationHeader')}`}
                                <div className={styles.datePickerLabel}>{t('Start Date')}</div>
                                <Field name='start'>
                                    {() => (
                                        <Datepicker
                                            type='single'
                                            value={values.start}
                                            size='sm'
                                            handleChange={(value) => {
                                                setFieldValue('start', value);
                                            }}
                                            editable
                                            // exlcude today and yesterday
                                            includeDates={allowedDateRange}
                                            // minDate={oldestAllowedDate}
                                            maxDate={latestAllowedDate}
                                        />
                                    )}
                                </Field>
                                {!hideCompare && (
                                    <>
                                        <div className={styles.datePickerLabel}>{t('compareSimulationResult')}</div>
                                        <Field name='compare'>
                                            {({ field }: FieldProps) => (
                                                <ButtonGroup
                                                    dataTestId='compare-radio'
                                                    className={styles.buttonGroup}
                                                    name='compare'
                                                    handleClickCallback={(value) => {
                                                        setFieldValue('compare', value === 'yes');
                                                    }}
                                                >
                                                    {!deploymentsData.length ? (
                                                        <Popover
                                                            popperClassName={styles.rulePopover}
                                                            position='top-start'
                                                            showOnHover
                                                            showArrow
                                                            arrowClassName={styles.rulePopoverArrow}
                                                            content={<div>{t('noRuleSetDeployed')}</div>}
                                                            contentClassName={styles.rulePopoverContent}
                                                        >
                                                            {renderRadio(field.value)}
                                                        </Popover>
                                                    ) : (
                                                        renderRadio(field.value)
                                                    )}
                                                    <RadioButton
                                                        name='no'
                                                        value='no'
                                                        checked={field.value === false}
                                                        text={t('No')}
                                                        id='compare-no'
                                                    />
                                                </ButtonGroup>
                                            )}
                                        </Field>
                                    </>
                                )}
                            </div>
                            <Modal.Footer
                                id='new-simulation-modal-footer'
                                onActionText={isLoading ? <Loading size='sm' fillName='white' /> : t('Run Simulation')}
                                disableOnAction={disableRunSimulation || isLoading}
                                onClose={closeModal}
                                onAction={submitForm}
                            />
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default NewSimulationModal;
